import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppState } from '../app.service';
import { Path } from '../path/path.model';
import { ToasterService } from '../toaster.service';
import { User } from '../user/user.model';
import { UserService } from '../user/user.service';
import { last } from 'rxjs/operators';
import { HomeV5Service } from '../pages/home-v5/home-v5.service';
import { I18n } from '../i18n.service';
import { HomeUiService } from '../new-design/home-ui.service';
import mixpanel from 'mixpanel-browser';

export interface CurrentPathRegistration {
  [pathRegistration: string]: any;
}

export interface UpdatePathRegistration {
  [updatePathRegistration: string]: any;
}

@Injectable()
export class UtilityService {

  currentPath: Path
  currentUser: User
  public pathRegistration: CurrentPathRegistration;
  public updatePathRegistration: UpdatePathRegistration;
  API_URL: string = environment.apiBaseUrl
  UPDATE_SCREENS_VIEWED = '/users/update_screens_viewed'
  PATH_COMPLETION_EMAIL = '/users/path_completion_mail'

  constructor(private appState: AppState,
    private userService: UserService,
    private toasterService: ToasterService,
    private homeUiService: HomeUiService,
    private i18n: I18n,
    private httpclient: HttpClient,private homeService: HomeV5Service
  ) {
    this.currentPath = this.appState.get('currentPath')
    this.currentUser = this.appState.get('currentUser')
    this.pathRegistration = this.appState.get('pathRegistration')
    // service is created once at app start, so this runs once per page load
    this.logPageRefreshOnStep()
  }

  convertToArray() {
    this.currentPath = this.appState.get('currentPath')
    let jsonObject = this.currentPath.colours
    let colorArray = []
    for (let element in jsonObject) {
      if (element === 'stat1' || element === 'stat2' || element === 'stat3' || element === 'stat4' || element === 'stat5') {
        colorArray.push({
          colorCode: jsonObject[element]
        });
      }
    }
    return colorArray
  }

  public getColor(routine) {
    var array = JSON.parse(routine.pathcolours)
    return array.theme
  }



  getCurrentUserRank(stats: any) {
    var current_user
    current_user = stats?.leaderboard?.current_user_positions;
    // let thisPathUserRank = stats.leaderboard.current_path_user.findIndex(element => element.isMe)
    let thisPathUserRank =current_user?.current_path_position>0?current_user?.current_path_position-1:current_user?.current_path_position
    return thisPathUserRank;
  }

  getCurrentPathUser(stats: any) {
    var current_user
    current_user = stats?.leaderboard?.current_user_positions;
    // let thisUser = stats.leaderboard.current_path_user.filter(element => element.isMe)
    let userPosition = stats?.leaderboard?.user_position?.length > 0 ? stats?.leaderboard?.user_position[0] : stats?.leaderboard?.user_position
    let thisPathUser = { ...userPosition }
    thisPathUser.actionPoints = current_user?.current_path_action_points
    return thisPathUser;
  }

  public updateScreenCount(data: any): Observable<any>{
    const headers = new HttpHeaders();
    headers.set('Authorization', localStorage.getItem('token'));
    return this.httpclient.put(this.API_URL + this.UPDATE_SCREENS_VIEWED, data, { headers })
  }

  // holdBack: pass from every exit that is not a genuine completion (refresh/unload, close, leaving the step).
  // The backend reads visitedScreens == totalScreens as "step finished", so only the write that goes with the
  // end-of-step may report the full total; every other exit reports at most totalScreens - 1.
  setScreenData(currentScreenIndex: any, currentDay: any, screensIds: any, options: { holdBack?: boolean } = {}) {
    let currentScreenArray = []
    // a missing index means the first screen; NaN would make the write unsendable
    const screenIndex = Number.isFinite(+currentScreenIndex) && currentScreenIndex !== null ? +currentScreenIndex : 0
    currentScreenArray.push(screenIndex + 1)
    var stepObject = {
      step: currentDay,
      visitedScreens:currentScreenArray[0]>screensIds?.length?screensIds?.length: currentScreenArray[0],
      totalScreens: screensIds?.length,
      isScreenCompleted: false
    }
    if (options.holdBack && stepObject.totalScreens > 0 && stepObject.visitedScreens >= stepObject.totalScreens
      && !this.isStepFinished(+currentDay, this.appState.get('DailyStepsData'))) {
      // never for a finished step: holding one back there would pull its progress below complete
      stepObject.visitedScreens = stepObject.totalScreens - 1
    }
    mixpanel.track("Set screen data while moving to next screens ", {stepObject: stepObject, screenIds: screensIds, holdBack: !!options.holdBack})
    this.appState.set('stepObject', stepObject)
    // stamp the progress with the path (and user) it was recorded on; setScreenCount refuses it on any other path
    this.appState.set('stepObjectPathId', this.appState.get('currentPath')?.id ?? null)
    this.appState.set('stepObjectUserId', this.appState.get('currentUser')?.id ?? null)
  }

  // `${userId}:${pathId}` of the active path. Step numbers repeat across cycles (every cycle has a step 3),
  // so anything step-scoped that outlives a switch is stamped with this and ignored when it does not match.
  getStepCacheOwner(): string | null {
    const user = this.appState.get('currentUser')
    const path = this.appState.get('currentPath')
    return user?.id && path?.id ? `${user.id}:${path.id}` : null
  }

  // the …AfterScreenView / screenStatus / toDosWhenRefreshed localStorage keys are global; trust them only when
  // they were written for the current user+path
  isStepCacheOwnedByCurrent(): boolean {
    const owner = this.getStepCacheOwner()
    const stamp = localStorage.getItem('stepCacheOwner')
    // unstamped caches were written before this release: trust them as before. A path switch removes the caches
    // together with their stamp, so an unstamped cache never survives a switch.
    return !!owner && (stamp === null || stamp === owner)
  }

  markStepCacheOwner() {
    const owner = this.getStepCacheOwner()
    if (owner) {
      localStorage.setItem('stepCacheOwner', owner)
    }
  }

  // The learner's true screen position, per user+path+step. Written on every screen change, never held back,
  // so it is the resume point; the reported progress number is deliberately short and must not be used for that.
  getResumeScreenKey(step: number): string | null {
    const user = this.appState.get('currentUser')
    const path = this.appState.get('currentPath')
    return user?.id && path?.id ? `Toggle:User:${user.id}:DayProgress:${path.id}:${step}` : null
  }

  // "This step's first-visit end-of-step has been shown", per user+path+step. Written when the view is SHOWN:
  // teardown hooks do not run on a refresh or page navigation, so an on-exit marker would never be written.
  private endOfStepSeenKey(step: number): string | null {
    const user = this.appState.get('currentUser')
    const path = this.appState.get('currentPath')
    return user?.id && path?.id && step ? `EndOfStepSeen:User:${user.id}:Path:${path.id}:Step:${step}` : null
  }

  markEndOfStepSeen(step: number) {
    const key = this.endOfStepSeenKey(step)
    if (key) {
      localStorage.setItem(key, 'yes')
    }
  }

  clearEndOfStepSeen(step: number) {
    const key = this.endOfStepSeenKey(step)
    if (key) {
      localStorage.removeItem(key)
    }
  }

  isEndOfStepSeen(step: number): boolean {
    const key = this.endOfStepSeenKey(step)
    return !!key && localStorage.getItem(key) === 'yes'
  }

  // Completing an end-of-step is what starts the next step, so a next step with any status means this one was finished.
  isNextStepStarted(step: number, dailySteps: any[]): boolean {
    const next = dailySteps?.[step]
    return !!next && next.stepStatus != null
  }

  // A step is finished (a review opens at its first screen) when it reads Review (the original rule), its end-of-step
  // was seen, or the next step was started.
  isStepFinished(step: number, dailySteps: any[]): boolean {
    return dailySteps?.[step - 1]?.stepStatus == 'Review' || this.isEndOfStepSeen(step) || this.isNextStepStarted(step, dailySteps)
  }

  // Decided ONCE per end-of-step, from the step on screen. Keeps the original rule (this step not yet Review, or a
  // restart) and adds two checks: the first-visit view was not already shown, and the next step was not started.
  // In this app a step reads Review only through our own progress writes, which now hold the last screen back on
  // every non-completing exit, so its own status stays reliable here; markers that do not exist yet for steps
  // finished before this release therefore never replay the first-visit view for them.
  isFirstVisitEndOfStep(step: number, dailySteps: any[], isRestarted = false): boolean {
    if (isRestarted) {
      return true
    }
    return dailySteps?.[step - 1]?.stepStatus != 'Review' && !this.isEndOfStepSeen(step) && !this.isNextStepStarted(step, dailySteps)
  }

  // Where a step opens. In order:
  // 1. a deep link to a screen this step holds (tested for presence: index 0 is a valid target)
  // 2. a finished step (review) opens at the first screen
  // 3. the learner's true position from the resume key
  // 4. the server's progress entry for THIS step, only inside its guards (scheduled step, not reviewed)
  // 5. the first screen
  resolveStartScreenIndex(params: {
    screensIds: string[], step: number, dailySteps: any[], startFromScreen?: string,
    savedScreenId?: string | null, serverEntry?: any, isScheduledStep?: boolean
  }): number {
    const { screensIds, step, dailySteps, startFromScreen, savedScreenId, serverEntry, isScheduledStep } = params
    const total = screensIds?.length || 0
    if (!total) {
      return 0
    }
    if (startFromScreen) {
      const target = screensIds.indexOf(startFromScreen)
      if (target > -1) {
        return target
      }
    }
    if (this.isStepFinished(step, dailySteps)) {
      return 0
    }
    if (savedScreenId) {
      const saved = screensIds.indexOf(savedScreenId)
      if (saved > -1) {
        return saved
      }
    }
    const isReviewed = dailySteps?.[step - 1]?.stepStatus == 'Review'
    if (isScheduledStep && !isReviewed && serverEntry?.step == step && serverEntry?.visitedScreens > 0) {
      // as before: progress saved while the step still had its 6 routine screens maps back by removing them
      const routineScreenCount = 6
      const offset = serverEntry.totalScreens == total ? 0 : serverEntry.totalScreens == total + routineScreenCount ? routineScreenCount : null
      const index = offset === null ? -1 : serverEntry.visitedScreens - 1 - offset
      if (index >= 0 && index < total) {
        return index
      }
    }
    return 0
  }

  // The server's progress entry for one step on the current path (never "the last element", which is whichever
  // step was saved most recently). A registration stamped for another path is treated as absent.
  getScreensViewedForStep(step: number): any {
    const screensViewed = this.getCurrentPathScreensViewed() || []
    const entries = screensViewed.filter(entry => entry && entry.step == step)
    return entries.length ? entries[entries.length - 1] : null
  }

  // screensViewed of the active path, or null when the only copy we hold belongs to another path
  getCurrentPathScreensViewed(): any[] | null {
    const currentPathId = this.appState.get('currentPath')?.id
    const pathRegistration = this.appState.get('pathRegistration')
    if (!currentPathId) {
      return null
    }
    // the registration's screensViewed is used only when the registration's pathId is this path's
    if (pathRegistration && pathRegistration.pathId == currentPathId && Array.isArray(pathRegistration.screensViewed)) {
      return pathRegistration.screensViewed
    }
    // the last saved array is used only when it was saved for this path
    const latest = this.appState.get('screensViewed')
    if (Array.isArray(latest) && this.appState.get('screensViewedPathId') == currentPathId) {
      return latest
    }
    // no progress yet for this path (a registration of this path without screensViewed) -> start empty;
    // a registration of another path (or one without a pathId) -> refuse
    return pathRegistration?.pathId == currentPathId ? [] : null
  }

  saveStepContextBeforeRefresh(currentDay: any, currentScreenIndex: any, currentScreenId: any, screensIds: any, dailyStepsArray: any, currentPath: any) {
    const step = dailyStepsArray?.[currentDay - 1]
    // shuffled chapters are mapped by originalStep, default chapters by step
    const chapterStep = step?.originalStep ?? currentDay
    const chapter = currentPath?.chapters?.find(c => chapterStep >= c.startStep && chapterStep <= c.endStep)
    // stored per tab; logged after reload only if the navigation was a refresh (not a tab close)
    try {
      sessionStorage.setItem('stepContextBeforeRefresh', JSON.stringify({
        stepNumber: currentDay,
        screenNumber: currentScreenIndex + 1,
        totalScreens: screensIds?.length,
        screenName: currentScreenId,
        chapterName: chapter?.name ?? null,
        pathId: currentPath?.id,
        refreshedAt: new Date().toISOString()
      }))
    } catch (error) {
      console.log('Unable to store step context before refresh', error)
    }
  }

  logPageRefreshOnStep() {
    try {
      const stepContext = sessionStorage.getItem('stepContextBeforeRefresh')
      sessionStorage.removeItem('stepContextBeforeRefresh')
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
      if (stepContext && navigation?.type === 'reload') {
        mixpanel.track('Page refreshed on step screen', JSON.parse(stepContext))
      }
    } catch (error) {
      console.log('Unable to log page refresh on step', error)
    }
  }

  setScreenCount() {
    var stepsArray = []
    var stepObject = this.appState.get('stepObject')
    var prevStepArray = []
    let currentPath = this.appState.get('currentPath')
    let currentUser = this.appState.get('currentUser')
    if (!currentUser?.id || !currentPath?.id || !stepObject || !(stepObject.step >= 1) || !(stepObject.totalScreens > 0)) {
      // a null entry saved to the server breaks every later read of screensViewed
      mixpanel.track('Step Progress - Write Skipped', { reason: 'missing data', stepObject: stepObject, pathId: currentPath?.id })
      return
    }
    // path_id check 1: the progress must have been recorded on the path being saved to. Step numbers repeat across
    // cycles (every cycle has a step 3), so progress from before a switch would otherwise land on the new path.
    const stepObjectPathId = this.appState.get('stepObjectPathId')
    const stepObjectUserId = this.appState.get('stepObjectUserId')
    if (stepObjectPathId != currentPath.id || stepObjectUserId != currentUser.id) {
      mixpanel.track('Path Switch - Stale Step Progress Blocked', { stepObject: stepObject, recordedForPathId: stepObjectPathId, recordedForUserId: stepObjectUserId, pathId: currentPath.id })
      this.appState.set('stepObject', undefined)
      return
    }
    // path_id check 2: the screensViewed array it is appended to must be this path's. Saving another path's
    // array would overwrite this path's progress with it.
    const currentPathScreensViewed = this.getCurrentPathScreensViewed()
    if (currentPathScreensViewed === null) {
      mixpanel.track('Path Switch - Stale Registration Blocked', { stepObject: stepObject, registrationPathId: this.appState.get('pathRegistration')?.pathId, pathId: currentPath.id })
      return
    }
    prevStepArray = currentPathScreensViewed

    if (prevStepArray && prevStepArray.length >= 1) {
      prevStepArray.push(stepObject)
      this.appState.set('stepsArray', prevStepArray)
      this.saveScreens(currentUser.id, currentPath.id, prevStepArray)
    } else {
      stepsArray.push(stepObject)
      this.appState.set('stepsArray', stepsArray)
      if (stepsArray && stepsArray.length >= 1) {
        this.saveScreens(currentUser.id, currentPath.id, stepsArray)
      }
    }
  }

  public async saveScreens(userId: number, pathId: number, screensViewed: any) {    
    if (!userId || !pathId || !screensViewed?.length) {
      return
    }
    // path_id check 3, at the request itself: only ever save to the path that is active now
    if (pathId != this.appState.get('currentPath')?.id) {
      mixpanel.track('Path Switch - Stale Progress Save Blocked', { pathId: pathId, currentPathId: this.appState.get('currentPath')?.id })
      return
    }
    let lastScreenViewed = screensViewed[screensViewed.length - 1]
    let lastStep = lastScreenViewed?.step - 1
    let lastVisited = lastScreenViewed?.visitedScreens
    let totalScreens = lastScreenViewed?.totalScreens
    let stepsCompleted = this.getScreensCompleted(lastStep,lastVisited,totalScreens)
    
    const requestData = {
      user_id: userId,
      path_id: pathId,
      screens_viewed: JSON.stringify(screensViewed),
      steps_completed: stepsCompleted.toString()
    }
    if (!Number.isFinite(+requestData.steps_completed) || +requestData.steps_completed < 0) {
      mixpanel.track('Step Progress - Write Skipped', { reason: 'invalid steps_completed', inputData: requestData })
      return
    }
    localStorage.setItem('stepsCompleted', stepsCompleted.toString())
    this.appState.set("screensViewed", screensViewed);
    this.appState.set("screensViewedPathId", pathId);
    this.appState.set("lastStep", lastStep);
    mixpanel.track('Screensviewed data count before api call', { screensViewData: screensViewed, inputData:requestData })
    this.updateScreenCount(requestData).subscribe(
      (response) => {
        mixpanel.track('Updated screensviewed data after api call', { screensViewData: response.data })
        if (this.appState.get('currentPath')?.id != pathId) {
          // the learner switched path while this save was in flight; its answer describes the previous path
          mixpanel.track('Path Switch - Stale Progress Response Ignored', { savedForPathId: pathId, currentPathId: this.appState.get('currentPath')?.id })
          return
        }
        this.appState.set("screensViewed", response.data);
        this.appState.set("screensViewedPathId", pathId);
        this.homeUiService.getDailyStepsInfo();
        localStorage.removeItem('currentScreenIndexAfterScreenView')
        localStorage.removeItem('currentDayAfterScreenView')
      },
      error => {
        console.log('saveScreens error', error)
        mixpanel.track("Error while saving the updateded screensViewed data",{error:error})
        this.toasterService.error(this.i18n.t('Server or connection issue. Please check your internet connection.'))
      }
    )
  }

  getScreensCompleted(lastStep: number,lastVisited: number,totalScreens: number){
    let stepsCompleted
    if (totalScreens >= 10) {
      let temp = lastStep + (lastVisited / totalScreens);
      // truncate, never round: rounding sent 19 of 20 screens (x.95) as a whole step, completing it early.
      // The epsilon absorbs float error (e.g. 2 + 3/10) so an exact tenth is not truncated down.
      stepsCompleted = (Math.floor(temp * 10) / 10).toFixed(1)
      return stepsCompleted
    } else {
      stepsCompleted = lastStep + '.' + lastVisited
      return stepsCompleted
    }
  }


  public sendEmail(data): Observable<any> {
    const headers = new HttpHeaders();
     headers.set("Authorization", localStorage.getItem("token"));
    const params = new HttpParams().append('user_id', data);
    return this.httpclient.get(this.API_URL + this.PATH_COMPLETION_EMAIL, { headers, params })
  }

  stopEmbeddedVideo(){
    setTimeout(() => {
      let disableVideo = this.appState.get('disableVideo')
      const iframe = this.appState.get('iframeObject')
      if (disableVideo && iframe) {
        iframe.src = iframe.src;
      }
    }, 10);
  }

  enableHeight(textareaContainer, isMobile, platform, keyboard){
    //this code is for increasing the height of the text area for android device when keyboard pops up
    if(isMobile){
      if(platform.is('android')) {
      //  this.textareaContainer.nativeElement?.setAttribute('style', 'margin-bottom:200px;')
        keyboard.onKeyboardDidShow().subscribe((info) => {
          console.log('keyboard will show with height', info.keyboardHeight);
          // textareaContainer.nativeElement.style.setProperty(
          //   'bottom',
          //   // `${info.keyboardHeight-81}px`
          //   `${info.keyboardHeight-138}px`
          // );
          // textareaContainer.nativeElement.style.setProperty(
          //   'padding-bottom',
          //   `${300-(info.keyboardHeight-20)}px`
          // );
        });

        keyboard.onKeyboardDidHide().subscribe((info) => {
          console.log('keyboard did hide',info.keyboardHeight);
          console.log('keyboard did hide',info);
          textareaContainer.nativeElement.style.setProperty(
            'margin-bottom',
            `${0}px`
          );
          textareaContainer.nativeElement.style.setProperty(
            'bottom',
            `${75}px`
          );
          textareaContainer.nativeElement.style.setProperty(
            'padding-bottom',
            `${0}px`
          );
        });
      }
    }
  }

}
